import { apiErrorSlug } from "./error.js";
import { isSetsNotStoredError, isSetsRefusedError } from "./setsStored.js";
import { isSessionConflictError, isStaleEtagError } from "./workoutSave.js";

// The i18n key of a failed save, out of workoutSave.ts under the file-size
// cap: the logger's error line and the outbox's failed row.

/**
 * The translated message for a save the server refused, when it is one the
 * athlete can act on (#38): an i18n key under `mobile.logger.`, or undefined
 * to show the server's own summary. One table for the logger's error line and
 * the outbox's failed row.
 */
export const saveErrorKey = (err: unknown): string | undefined => {
  const name = failureName(err);
  return name && `mobile.logger.${name}`;
};

/** The same classification for the outbox's failed row (the bell): its sheet
 *  offers "Discard" and no way to edit or save again, so a message that asks
 *  for that action has a variant under `mobile.outboxDetail` that says the
 *  fact only. The others keep the logger's key. */
export const outboxErrorKey = (err: unknown): string | undefined => {
  const name = failureName(err);
  if (!name) return undefined;
  return `mobile.${BELL_FACTS.has(name) ? "outboxDetail" : "logger"}.${name}`;
};

/** Messages with a fact-only twin under `mobile.outboxDetail`. */
const BELL_FACTS = new Set([
  "errSetsUnknownExercise",
  "errSetsInvalidTag",
  "errSetsInvalid",
  "errSetsNotSaved",
  "errCorrectionTooBig",
  "errCorrectionBusy",
  "errCorrectionConflict",
  "errCorrectionTimes",
  "errCorrectionGone",
  "errCorrectionStale",
]);

/** The key (under `mobile.logger.`) of the refusal, if the athlete can act on it. */
const failureName = (err: unknown): string | undefined => {
  // Sets the server refused for good (#289): the one the athlete can fix first.
  if (isSetsRefusedError(err)) {
    if (err.statuses.includes("unknown_exercise")) return "errSetsUnknownExercise";
    if (err.statuses.includes("invalid_tag")) return "errSetsInvalidTag";
    return "errSetsInvalid";
  }
  // Sets not stored this time (assertSetsStored): saving again may store them.
  if (isSetsNotStoredError(err)) return "errSetsNotSaved";
  if (isSessionConflictError(err))
    return err.slug === "endedAt_before_startedAt"
      ? "errCorrectionTimes"
      : "errCorrectionConflict";
  // Saved again since the correction began (#314).
  if (isStaleEtagError(err)) return "errCorrectionStale";
  switch (apiErrorSlug(err)) {
    case "too_many_changes":
      return "errCorrectionTooBig";
    case "concurrent_modification":
      return "errCorrectionBusy";
    case "import_cannot_replace":
      return "errCorrectionImport";
    // Only a correction answers our 404 `not_found` on a save (#38 M).
    case "not_found":
      return "errCorrectionGone";
    default:
      return undefined;
  }
};
